// The message on any page when one of my fetches finishes (Yehuda, Oct 25): a VA can press Fetch,
// leave the Record and keep working; when the fetch ends, a toast says so with Open. Asks the server
// every few seconds while one of my fetches is open, every half minute otherwise.
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useRevalidator } from 'react-router';
import type { Api } from '../auth/api';
import { finishedText } from '../renewals/fetch-lines';
import { Toast } from './Toast';

/** The Record sends this when Fetch is pressed, so the next check comes at once. */
export const fetchStartedEvent = 'ss:fetch-started';

const busyMs = 4000;
const idleMs = 30_000;
const shownMs = 10_000;

export function FetchNotices({ api }: { api: Api }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const pathRef = useRef(pathname);
  useEffect(() => {
    pathRef.current = pathname;
  }, [pathname]);
  // A finished fetch changes the lists (Fetching tag, Needs attention): reload the page's data.
  const revalidator = useRevalidator();
  const revalidateRef = useRef(revalidator.revalidate);
  useEffect(() => {
    revalidateRef.current = revalidator.revalidate;
  }, [revalidator]);
  const [toast, setToast] = useState<{ id: number; text: string; renewalId: string } | null>(null);

  useEffect(() => {
    let since: string | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let busy = false;
    let stopped = false;

    const check = async () => {
      if (busy || stopped) return;
      busy = true;
      clearTimeout(timer);
      let next = idleMs;
      try {
        const mine = await api.myFetches(since);
        if (mine.finished.length > 0) void revalidateRef.current();
        // The Record already shows its own fetch: no message for the one on screen.
        const shown = mine.finished.filter((f) => pathRef.current !== `/records/${f.renewalId}`);
        const last = shown.at(-1);
        if (last) {
          const more = shown.length > 1 ? `${shown.length} fetches finished. ` : '';
          setToast({ id: Date.now(), text: more + finishedText(last), renewalId: last.renewalId });
        }
        since = mine.now;
        next = mine.open > 0 ? busyMs : idleMs;
      } catch {
        // A missed check is fine: the next one asks again from the same moment.
      } finally {
        busy = false;
      }
      if (!stopped) timer = setTimeout(() => void check(), next);
    };

    const started = () => {
      clearTimeout(timer);
      timer = setTimeout(() => void check(), busyMs);
    };
    void check();
    window.addEventListener(fetchStartedEvent, started);
    return () => {
      stopped = true;
      clearTimeout(timer);
      window.removeEventListener(fetchStartedEvent, started);
    };
  }, [api]);

  if (!toast) return null;
  return (
    <Toast
      key={toast.id}
      text={toast.text}
      shownMs={shownMs}
      action={{ label: 'Open', onClick: () => navigate(`/records/${toast.renewalId}`) }}
    />
  );
}
